import { AppShell, Badge, Banner, Button, Drawer, Logo, MenuButton, Page, PageHeader, Panel, Select, Stack, Tabs, TextField } from '@ui';
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { setWorkingProject } from '../api';
import BugReportSettings, { TrackerAdmin, type ProjectPart } from '../BugReportSettings';
import { setConfig } from '../config';
import { ThemesView } from '../board/ThemesView';
import { HeaderSlot, HeaderSlotProvider } from './HeaderActions';
import { HostKey } from './HostKey';
import { NAV_ICONS } from './navIcons';
import { ProjectFeatures } from './ProjectFeatures';
import { TrackerVersions } from './TrackerVersions';
import { hashOfPlace, placeOfHash, sectionsFor, tabsOf, type Place } from './structure';
import UxSessions from '../ux/UxSessions';
import WhatsNewScreen from '../whatsNew/WhatsNewScreen';

// tracker.jaiora.me: login, top bar (tabs, who, password, logout) and the tracker screen — the page itself only loads
// widget.js and calls mountTracker (server/src/tracker/page.ts).

interface Me {
  user: string;
  project: string;
  /** Пр1: projects the person may work in; more than one — the header offers the switcher. */
  projects?: string[];
  isSuper: boolean;
  readsAll: boolean;
  manager: boolean;
  /** Пр5: registers projects and keys, grants the operator role. */
  operator?: boolean;
}

// `news` — «Что нового» of the tracker itself (Хс2): entered from the help menu, not a nav item.
// Мн1–Мн7: sections Задачи (Доска, Список), Эпики (Бэклог, В работе), Вопросы, UX-сессии, Проект (вкладки), Реестр проектов;
// the tree is `structure.ts`, the old addresses (#backlog, #board, #all, #epicbacklog, #settings …) lead to its places.

/** Build version of this tab (Хс4): the page carries the service's short sha (server/src/tracker/page.ts). */
const tabVersion = (): string | null => document.querySelector<HTMLMetaElement>('meta[name="jaiora-version"]')?.content || null;

async function post(url: string, body: unknown): Promise<Response> {
  return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body ?? {}) });
}

async function errorOf(res: Response): Promise<string> {
  const data = (await res.json().catch(() => null)) as { error?: string } | null;
  return data?.error || 'Не получилось — повторите.';
}

const PROJECT_KEY = 'tracker:working-project';

/** The remembered project, if the person may still work in it; the choice survives reloads in this browser. */
function rememberedProject(me: Me): string {
  const allowed = me.projects?.length ? me.projects : [me.project];
  try {
    const saved = window.localStorage.getItem(PROJECT_KEY);
    if (saved && allowed.includes(saved)) return saved;
  } catch {
    // Storage may be blocked: the own project it is.
  }
  return me.project;
}

/** Тг1: the tabs of the project's own settings; the rest (access, people, host key) are tabs of the shared area. */
const sectionOfTab = (tab: string): string => (['profile', 'voice', 'features'].includes(tab) ? 'project' : 'registry');

const HOME: Place = { section: 'tasks', tab: 'board' };

/** Where the address leads; unknown or empty — the board. */
const placeOfLocation = (): Place => placeOfHash(window.location.hash) ?? HOME;

/** Where a person lands when the place is not theirs: the first section of their menu. */
function reachable(place: Place, me: Pick<Me, 'isSuper' | 'operator'>): Place {
  if (place.section === 'news') return place;
  const sections = sectionsFor(me);
  const section = sections.find((item) => item.key === place.section);
  if (!section) {
    const first = sections[0];
    return { section: first.key, tab: tabsOf(first, me)[0]?.key ?? '' };
  }
  const tabs = tabsOf(section, me);
  if (tabs.length && !tabs.some((tab) => tab.key === place.tab)) return { section: section.key, tab: tabs[0].key };
  return place;
}

/** The sections of the menu for this person; every one carries its own icon (Рз1), the project ones stand under the project (Мн5). */
export function trackerSections(
  me: Pick<Me, 'isSuper' | 'operator'>,
  working?: string,
): { key: string; label: string; icon: ReactNode; group: string }[] {
  return sectionsFor(me).map((section) => ({
    key: section.key,
    label: section.key === 'questions' && !me.isSuper ? 'Мои вопросы' : section.label,
    icon: NAV_ICONS[section.key],
    // Мд5: the group names what it is — «Проект · facts», not a bare name.
    // Тг6: the shared area of the tracker stands apart from the project's own sections.
    group: section.group === 'tracker' ? 'Все проекты' : working ? `Проект · ${working}` : 'Проект',
  }));
}

export default function TrackerApp() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);

  useEffect(() => {
    fetch('/auth/me')
      .then(async (res) => setMe(res.ok ? ((await res.json()) as Me) : null))
      .catch(() => setMe(null));
  }, []);

  if (me === undefined) return null;
  return <div className="jui-app bug-tracker-app">{me ? <Workspace me={me} /> : <Login onIn={setMe} />}</div>;
}

function Login({ onIn }: { onIn: (me: Me) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await post('/auth/login', { username, password });
      if (!res.ok) {
        setError(await errorOf(res));
        return;
      }
      onIn((await res.json()) as Me);
    } catch {
      setError('Сервис не отвечает — повторите.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bug-tracker-login" data-theme="light">
      <div className="bug-tracker-login-brand">
        <Logo size={40} />
        <span>Трекер</span>
      </div>
      <Panel title="Вход">
        <Stack as="form" gap={3} onSubmit={submit}>
          <TextField label="Логин" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoFocus required />
          <TextField
            label="Пароль"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          {error && <Banner tone="error" title={error} />}
          <Button type="submit" variant="primary" busy={busy} disabled={!username.trim() || !password}>
            Войти
          </Button>
        </Stack>
      </Panel>
    </div>
  );
}

function Workspace({ me }: { me: Me }) {
  const projects = me.projects?.length ? me.projects : [me.project];
  const [working, setWorking] = useState<string>(() => {
    // Мн9: the address names the project (a link); else the one this browser remembers.
    const named = placeOfLocation().project;
    const initial = named && projects.includes(named) ? named : rememberedProject(me);
    setWorkingProject(initial === me.project ? null : initial);
    return initial;
  });
  /** The project the address names: only one that is not the person's own (Мн9). */
  const namedFor = useCallback((name: string) => (name === me.project ? undefined : name), [me.project]);
  const applyProject = useCallback(
    (name: string) => {
      setWorkingProject(name === me.project ? null : name);
      try {
        window.localStorage.setItem(PROJECT_KEY, name);
      } catch {
        // Not remembered, still chosen.
      }
      setWorking(name);
    },
    [me.project],
  );
  const [place, setPlace] = useState<Place>(placeOfLocation);
  const [password, setPassword] = useState(false);
  /** Ра2, Ра7: the place in the page header where the screen's action buttons stand. */
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Ра7: the scrollbar never changes the width of the page when a tab is taller or shorter than the window.
    const root = document.documentElement;
    const before = root.style.scrollbarGutter;
    root.style.scrollbarGutter = 'stable';
    return () => {
      root.style.scrollbarGutter = before;
    };
  }, []);

  useEffect(() => {
    // The standard help button with a bug report: what it sends goes to the project `tracker` (tracker_access П308).
    setConfig({
      launcher: { bugs: true },
      // Хс2: own «Что нового» — the notes of project tracker the deploy published to the service.
      whatsNew: {
        service: true,
        open: () => {
          window.location.hash = '#news';
        },
      },
      // Хс3: the page moves aside for the report panel.
      makeRoom: { marginRight: ['.bug-tracker'] },
      appVersion: tabVersion(),
      settings: { prompts: me.isSuper },
      onOpenUxSessions: () => {
        window.location.hash = '#ux';
      },
      onApiError: (err) => {
        if (err.status === 401) window.location.reload();
      },
    });
    const onHash = () => {
      const next = placeOfLocation();
      setPlace(next);
      // A link that names another project of this person opens that project (Мн9).
      if (next.project && projects.includes(next.project)) applyProject(next.project);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.isSuper, applyProject]);

  const go = useCallback(
    (next: Place) => {
      window.location.hash = hashOfPlace({ ...next, project: namedFor(working) });
      setPlace(next);
    },
    [working, namedFor],
  );
  // Т5: a place the person may not open falls back to their first section.
  const shown = reachable(place, me);
  const pick = useCallback(
    (key: string) => {
      const section = sectionsFor(me).find((item) => item.key === key);
      go(section ? { section: key, tab: tabsOf(section, me)[0]?.key ?? '' } : HOME);
    },
    [go, me],
  );

  // Мн9: a change of project keeps the section and the tab; the address follows it.
  const chooseProject = (name: string) => {
    applyProject(name);
    window.location.hash = hashOfPlace({ ...shown, project: namedFor(name) });
  };
  // Ра6: a tab of «Проект» of a named project — the project is picked first, so the tab opens for it and the address names it.
  const openProject = (name: string, tab: string) => {
    applyProject(name);
    const next: Place = { section: sectionOfTab(tab), tab };
    window.location.hash = hashOfPlace({ ...next, project: namedFor(name) });
    setPlace(next);
  };

  const logout = async () => {
    await post('/auth/logout', {});
    window.location.reload();
  };

  const sections = trackerSections(me, working);
  const sectionLabel = shown.section === 'news' ? 'Что нового' : (sections.find((section) => section.key === shown.section)?.label ?? 'Задачи');
  // Мд5: the screens of the project say whose project it is.
  const title = shown.section === 'project' ? `${sectionLabel} · ${working}` : sectionLabel;
  // Ур4: the heading is the section only; the level is the badge of Мн11.
  const def = sectionsFor(me).find((section) => section.key === shown.section);
  const tabs = def ? tabsOf(def, me) : [];

  return (
    <AppShell
      product="Трекер"
      className="bug-tracker"
      nav={sections}
      current={shown.section}
      onNavigate={pick}
      // ☰ at every width (library, CRM Бг1–Бг4): on a wide window it collapses the section column to icons and back;
      // the choice is remembered in this browser under the tracker's key.
      railKey="tracker:rail-collapsed"
      // Мн9: the project switcher stands once in the shell's bar, over the menu, not in the header of every page.
      account={
        <>
          {projects.length > 1 && (
            <Select
              label="Проект"
              hideLabel
              size="sm"
              className="bug-shell-project"
              value={working}
              onChange={chooseProject}
              options={projects.map((name) => ({ value: name, label: name }))}
            />
          )}
          <MenuButton
            label={`Учётная запись: ${me.user}`}
            text={me.user}
            variant="quiet"
            size="sm"
            items={[
              { label: [me.isSuper ? 'суперадмин' : 'админ', working].filter(Boolean).join(' · '), disabled: true, onSelect: () => undefined },
              { label: 'Сменить пароль', onSelect: () => setPassword(true) },
              { label: 'Выйти', danger: true, onSelect: () => void logout() },
            ]}
          />
        </>
      }
    >
      <Page width={shown.section === 'news' ? 'reading' : 'fluid'}>
        {/* Ра10: the title is one piece of text — a name is never split off to a second line when names are masked. */}
        <PageHeader
          title={<span className="bug-page-title">{title}</span>}
          actions={
            <>
              <HeaderSlot onNode={setHeaderSlot} />
              {streamOf(shown.section)}
            </>
          }
        />
        {tabs.length > 1 && (
          <Tabs
            label={`${title}: виды`}
            variant="pill"
            items={tabs.map((tab) => ({ key: tab.key, label: tab.label }))}
            value={shown.tab}
            onChange={(tab) => go({ section: shown.section, tab })}
          />
        )}
        <div key={working} className="bug-working-body">
          <HeaderSlotProvider value={headerSlot}>
            <SectionBody
              place={shown}
              me={me}
              working={working}
              projects={projects}
              onChoose={chooseProject}
              onOpenProject={openProject}
            />
          </HeaderSlotProvider>
        </div>
      </Page>
      <Drawer open={password} onClose={() => setPassword(false)} size="sm" title="Смена пароля">
        <PasswordForm onDone={() => setPassword(false)} />
      </Drawer>
    </AppShell>
  );
}

/** Мн11 👁: the stream of a section in 2–4 words — epics are the upstream (what is solved), tasks the downstream (what the agent does). */
const STREAMS: Record<string, { label: string; tip: string }> = {
  epics: { label: 'Апстрим', tip: 'Что решаем: эпики и их задачи' },
  tasks: { label: 'Даунстрим', tip: 'Что делает агент: задачи' },
};
function streamOf(section: string): ReactNode {
  const stream = STREAMS[section];
  return stream ? (
    <Badge tone="accent" tip={stream.tip}>
      {stream.label}
    </Badge>
  ) : undefined;
}

/** What a place shows: the screens of the tree, each block in one place (Мн1). */
function SectionBody({
  place,
  me,
  working,
  projects,
  onChoose,
  onOpenProject,
}: {
  place: Place;
  me: Me;
  working: string;
  projects: string[];
  onChoose: (name: string) => void;
  onOpenProject: (name: string, tab: string) => void;
}) {
  const { section, tab } = place;
  if (section === 'news') return <WhatsNewScreen service />;
  if (section === 'ux') return <UxSessions />;
  if (section === 'registry') {
    // Тг1: the shared area — projects (the operator), access MCP, people, host keys; the choice of projects lives in the tabs.
    if (tab === 'versions') return <TrackerVersions />;
    if (tab === 'host') return <HostKey project={working} projects={projects} onChoose={onChoose} />;
    const shared = tab === 'mcp' ? 'tokens' : tab === 'users' ? 'users' : 'projects';
    return <TrackerAdmin section={shared} operator={!!me.operator} selected={{ openProject: onOpenProject }} />;
  }
  if (section === 'project' && tab === 'features') return <ProjectFeatures project={working} />;
  if (section === 'project') return <BugReportSettings key={tab} tracker section="project" part={tab as ProjectPart} />;
  if (section === 'questions') return <BugReportSettings tracker section="questions" />;
  if (section === 'epics') return <BugReportSettings key={tab} tracker section={tab === 'backlog' ? 'epicBacklog' : 'epics'} />;
  if (tab === 'themes') return <ThemesView onError={(err, fallback) => console.error(fallback, err)} />;
  if (tab === 'list') return <BugReportSettings key={place.stage ?? 'all'} tracker section="taskList" stage={place.stage} />;
  return <BugReportSettings tracker section="board" />;
}

function PasswordForm({ onDone }: { onDone: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await post('/auth/password', { current, next });
      if (!res.ok) {
        setError(await errorOf(res));
        return;
      }
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack as="form" gap={3} onSubmit={submit}>
      <TextField label="Текущий" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
      <TextField
        label="Новый"
        hint="от 8 символов"
        type="password"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        autoComplete="new-password"
        minLength={8}
        required
      />
      {error && <Banner tone="error" title={error} />}
      <Button type="submit" variant="primary" size="sm" busy={busy} disabled={!current || next.length < 8}>
        Сменить
      </Button>
    </Stack>
  );
}
